import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Chip,
  Modal,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  AccountBox as AccountBoxIcon,
  Schedule as ScheduleIcon,
  Build as BuildIcon,
  Group as GroupIcon,
  Event as EventIcon,
  Language as LanguageIcon,
  Public as PublicIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useTour from "../hooks/useTour";
import { getTourConfig } from "../config/tourConfigs";
import { useContext } from "react";
import { OnboardingContext } from "../contexts/OnboardingContext";
import { updateOnboardingStep } from "./dataProvider";
import { useTourContext } from "../contexts/TourContext";
import { ProviderContext } from "../ProviderContext";

const OnboardingSheet = ({ onClose }) => {
  const navigate = useNavigate();
  const { startPageTour } = useTour();
  // Si onboardingSteps no existe, fallback a []
  const {
    onboardingSteps,
    setOnboardingSteps,
    shouldOpenOnboarding,
    setShouldOpenOnboarding,
    checkAndTriggerOnboarding,
  } = useContext(OnboardingContext);
  const onboardingStepsCompleted = onboardingSteps ? onboardingSteps : [];

  // Contexto del tour para manejar el paso 3
  const { setStep3Active } = useTourContext();

  // Obtener el provider del contexto
  const { provider } = useContext(ProviderContext);

  // Estado para el modal del sitio web
  const [showWebsiteModal, setShowWebsiteModal] = useState(false);

  // Verificación temprana: si ya completó los 7 pasos, no renderizar el componente
  const totalSteps = 7;
  const completedStepsCount = onboardingStepsCompleted.length;
  const isOnboardingComplete = completedStepsCount >= totalSteps;

  // Si el onboarding está completo, no renderizar nada
  if (isOnboardingComplete) {
    return null;
  }
  const perfil = localStorage.getItem("profile");
  const profile = perfil ? JSON.parse(perfil) : null;
  // Construir la URL del sitio web usando el contexto del provider
  console.log("PROVEEDOR DESDE ONBOARDING:", provider);
  const websiteUrl = provider?.slugUrl ? `${process.env.REACT_APP_WEB_CLIENT_URL}/${provider.slugUrl}` : "";
  // Funciones para el modal del sitio web
  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(websiteUrl);
      // Podrías agregar un toast o notificación aquí
    } catch (err) {
      // Error handling
    }
  };

  const handleOpenWebsite = () => {
    if (websiteUrl) {
      window.open(websiteUrl, "_blank");
    }
  };

  const handleFinishOnboarding = async () => {
    try {
      const userId = profile?._id;
      // const resp = await updateOnboardingStep({ userId: userId, step: 7 });
      // setOnboardingSteps(resp.data ?? []);
      await updateOnboardingStep(
        { userId: userId, step: 7 },
        { setOnboardingSteps, checkAndTriggerOnboarding, onboardingSteps }
      );
      setShowWebsiteModal(false);
      setShouldOpenOnboarding(false);
      if (onClose) onClose();
    } catch (err) {
      // Error handling
      throw new Error(err.message || "Error desconocido");
    }
  };

  // Configuración de los pasos del onboarding, cada uno con stepNumber
  const onboardingStepsList = [
    {
      stepNumber: 1,
      id: "create-account",
      title: "Crea tu cuenta en Te Ayudo",
      description: "Regístrate y accede a todas las funcionalidades.",
      icon: <LanguageIcon />,
      route: "/register",
      tourSteps: [],
    },
    {
      stepNumber: 2,
      id: "account-setup",
      title: "Configura tu perfil de negocio",
      description: "Completa los datos de tu perfil empresarial.",
      icon: <AccountBoxIcon />,
      route: "/my-profile",
      tourSteps: [
        {
          element: "[data-tour='facebook-config']",
          popover: {
            title: "Configuración de Facebook",
            description: "Conecta tu perfil de Facebook Business aquí",
          },
        },
      ],
    },
    {
      stepNumber: 3,
      id: "first-service",
      title: "Configura tu primer servicio",
      description: "Crea el primer servicio que ofrecerás a tus clientes",
      icon: <BuildIcon />,
      route: "/service",
      tourSteps: [
        {
          element: "[data-tour='crear-servicio']",
          popover: {
            title: "Crear Servicio",
            description: "Define los detalles de tu primer servicio",
          },
        },
      ],
    },
    {
      stepNumber: 4,
      id: "business-hours",
      title: "Configura tu horario de atención",
      description: "Define cuándo estás disponible para tus clientes",
      icon: <ScheduleIcon />,
      route: "/availabilityWeb",
      tourSteps: [
        {
          element: "[data-tour='horario-atencion']",
          popover: {
            title: "Horario de Atención",
            description: "Establece tus horarios de trabajo disponibles",
          },
        },
      ],
    },
    {
      stepNumber: 5,
      id: "branch-setup",
      title: "Configura tu sucursal",
      description: "Completa los datos de tu sucursal para comenzar a operar",
      icon: <EventIcon />,
      route: "/enterprise/branches/create",
      tourSteps: [
        {
          element: "[data-tour='configura-sucursal']",
          popover: {
            title: "Configura tu Sucursal",
            description: "Completa los datos de tu sucursal para comenzar a operar.",
          },
        },
      ],
    },
    {
      stepNumber: 6,
      id: "employee-setup",
      title: "Crea tus empleados",
      description: "Agrega los empleados que atenderán en tu sucursal",
      icon: <GroupIcon />,
      route: "/employee/create",
      tourSteps: [
        {
          element: "[data-tour='crea-empleados']",
          popover: {
            title: "Crea tus Empleados",
            description: "Agrega los empleados que atenderán en tu sucursal.",
          },
        },
      ],
    },
    {
      stepNumber: 7,
      id: "website-setup",
      title: "Conoce tu sitio web para clientes",
      description: "Descubre cómo tus clientes pueden hacer reservas en línea",
      icon: <PublicIcon />,
      route: null, // No navega, abre modal
      tourSteps: [],
    },
  ];

  // Progreso visual
  const completedCount = onboardingStepsList.filter((step) =>
    onboardingStepsCompleted.includes(step.stepNumber)
  ).length;
  const progress = {
    completed: completedCount,
    total: onboardingStepsList.length,
    percentage: Math.round((completedCount / onboardingStepsList.length) * 100),
  };

  // Verifica si el paso está completado según el número
  const isStepCompleted = (stepNumber) => onboardingStepsCompleted.includes(stepNumber);

  // Verifica si un paso está bloqueado (lógica en cascada)
  const isStepBlocked = (stepNumber) => {
    // El primer paso nunca está bloqueado
    if (stepNumber === 1) {
      return false;
    }
    // Todos los demás pasos requieren que el anterior esté completado
    return !isStepCompleted(stepNumber - 1);
  };

  // Obtiene el mensaje de bloqueo para un paso
  const getBlockedMessage = (stepNumber) => {
    if (stepNumber === 1) {
      return "";
    }
    return `Debe completar el paso ${stepNumber - 1} primero`;
  };

  const handleStartStep = (step) => {
    // Verificar si el paso está bloqueado
    if (isStepBlocked(step.stepNumber)) {
      return; // No hacer nada si está bloqueado
    }

    if (step.id === "website-setup") {
      // Caso especial para el paso 7: abrir modal
      setShowWebsiteModal(true);
      return;
    }

    // Activar isStep3Active si es el paso 3 (first-service)
    if (step.id === "first-service") {
      setStep3Active(true);
    }

    navigate(step.route);
    setShouldOpenOnboarding(false);
    if (onClose) onClose();
    // Obtener la configuración del tour según el id
    const tourConfig = getTourConfig(step.id);
    if (tourConfig && tourConfig.steps && tourConfig.steps.length > 0) {
      setTimeout(() => {
        startPageTour(step.id, tourConfig.steps);
      }, 500);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={shouldOpenOnboarding}
      onClose={() => {
        setShouldOpenOnboarding(false);
        if (onClose) onClose();
      }}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: "420px", md: "480px" },
          maxWidth: "100vw",
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#252526",
            color: "white",
          }}
        >
          {/* Círculo de progreso */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              {/* Círculo de fondo */}
              <CircularProgress
                variant="determinate"
                value={100}
                size={45}
                thickness={3.5}
                sx={{
                  color: "rgba(255, 255, 255, 0.15)",
                  position: "absolute",
                }}
              />
              {/* Círculo de progreso */}
              <CircularProgress
                variant="determinate"
                value={(progress.completed / progress.total) * 100}
                size={45}
                thickness={3.5}
                sx={{
                  color: "white",
                  transform: "rotate(-90deg) !important",
                }}
              />
              {/* Texto del progreso */}
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: "700",
                    color: "white",
                  }}
                >
                  {progress.completed}/{progress.total}
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography variant="h5" component="h2" fontWeight="600" sx={{ fontSize: "1.4rem" }}>
                Primeros pasos
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9, fontSize: "0.875rem" }}>
                Has completado {progress.completed} de {progress.total} tareas
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={() => {
                setShouldOpenOnboarding(false);
                if (onClose) onClose();
              }}
              sx={{
                color: "white",
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Progress Bar */}
        {/* <Box sx={{ p: 3, borderBottom: 1, borderColor: "grey.100", bgcolor: "#fafafa" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
            Progreso del onboarding
          </Typography>
          <Box
            sx={{
              height: 8,
              bgcolor: "grey.200",
              borderRadius: 6,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Box
              sx={{
                height: "100%",
                background: "linear-gradient(90deg, #f57c00 0%, #ff9800 50%, #ffb74d 100%)",
                width: `${progress.percentage}%`,
                transition: "width 0.4s ease",
                borderRadius: 6,
              }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {progress.percentage}% completado
          </Typography>
        </Box> */}

        {/* Steps List */}
        <Box sx={{ flex: 1, overflow: "auto", bgcolor: "white" }}>
          <Typography variant="h6" sx={{ p: 3, pb: 2, color: "text.primary", fontWeight: 600, fontSize: "1.1rem" }}>
            Completa los primeros pasos y descubre más herramientas
          </Typography>

          <Box sx={{ px: 2, pb: 2 }}>
            {onboardingStepsList.map((step) => {
              const isCompleted = isStepCompleted(step.stepNumber);
              const isBlocked = isStepBlocked(step.stepNumber);

              return (
                <Box key={step.id} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: isCompleted ? "success.light" : isBlocked ? "grey.300" : "grey.200",
                      borderRadius: 3,
                      overflow: "hidden",
                      bgcolor: isCompleted ? "success.50" : isBlocked ? "error.50" : "white",
                      opacity: isBlocked ? 0.6 : 1,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: isCompleted ? "success.main" : isBlocked ? "error.light" : "primary.light",
                        boxShadow: isCompleted
                          ? "0 2px 8px rgba(76, 175, 80, 0.15)"
                          : isBlocked
                            ? "none"
                            : "0 2px 8px rgba(0,0,0,0.08)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 2.5,
                        cursor: isCompleted || isBlocked ? "default" : "pointer",
                      }}
                      onClick={!isCompleted && !isBlocked ? () => handleStartStep(step) : undefined}
                    >
                      {/* Número de paso e ícono */}
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "50%",
                          bgcolor: isCompleted ? "success.main" : "grey.100",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 2.5,
                          flexShrink: 0,
                          border: isCompleted ? "none" : "2px solid #e0e0e0",
                          transition: "all 0.2s ease",
                        }}
                      >
                        {isCompleted ? (
                          <CheckCircleIcon sx={{ color: "white", fontSize: 24 }} />
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 600,
                              fontSize: "0.875rem",
                            }}
                          >
                            {step.stepNumber}
                          </Typography>
                        )}
                      </Box>

                      {/* Contenido */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: isCompleted ? "success.dark" : "text.primary",
                            mb: 0.5,
                            fontSize: "0.95rem",
                            lineHeight: 1.4,
                          }}
                        >
                          {step.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isBlocked ? "error.main" : "text.secondary",
                            fontSize: "0.85rem",
                            lineHeight: 1.4,
                          }}
                        >
                          {isBlocked ? getBlockedMessage(step.stepNumber) : step.description}
                        </Typography>
                      </Box>

                      {/* Botón o estado */}
                      <Box sx={{ ml: 2, flexShrink: 0 }}>
                        {isCompleted ? (
                          <Chip
                            label="Completado"
                            size="small"
                            sx={{
                              bgcolor: "success.main",
                              color: "white",
                              fontWeight: 500,
                              fontSize: "0.75rem",
                              "& .MuiChip-label": {
                                px: 1.5,
                              },
                            }}
                          />
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<PlayArrowIcon />}
                            sx={{
                              bgcolor: "primary.main",
                              color: "white",
                              fontWeight: 500,
                              px: 2,
                              py: 0.75,
                              borderRadius: 2,
                              textTransform: "none",
                              fontSize: "0.875rem",
                              "&:hover": {
                                bgcolor: "primary.dark",
                                transform: "translateY(-1px)",
                                boxShadow: "0 4px 12px rgba(255, 119, 13, 0.3)",
                              },
                              transition: "all 0.2s ease",
                            }}
                          >
                            Iniciar
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Modal del sitio web */}
      <Modal
        open={showWebsiteModal}
        aria-labelledby="website-modal-title"
        aria-describedby="website-modal-description"
        disableEscapeKeyDown
        BackdropProps={{
          onClick: (event) => {
            event.stopPropagation();
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 500 },
            bgcolor: "white",
            borderRadius: 3,
            boxShadow: 24,
            p: 4,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          {/* Título */}
          <Typography id="website-modal-title" variant="h5" sx={{ mb: 2, fontWeight: 700, textAlign: "center" }}>
            ¡Conoce tu sitio web para clientes!
          </Typography>

          {/* Video de YouTube */}
          <Box sx={{ mb: 3 }}>
            <iframe
              width="100%"
              height="250"
              style={{ borderRadius: 8 }}
              // src="https://www.youtube.com/embed/K9zI9y-Y_WU?autoplay=1"
              src="https://www.youtube.com/embed/GY9B0Tq_GBg?autoplay=1"
              title="Video sitio web para clientes"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </Box>

          {/* Descripción */}
          <Typography id="website-modal-description" variant="body1" sx={{ mb: 3, textAlign: "center" }}>
            Con Te Ayudo tendrás un sitio web personalizable. Aumenta ingresos y reduce inasistencias con agendamiento y
            pago en línea.
          </Typography>

          <Typography variant="body1" sx={{ mb: 3, textAlign: "center", fontWeight: 600 }}>
            ¡Copia tu link y compártelo por WhatsApp y redes sociales!
          </Typography>

          {/* Campo de URL con botón copiar */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="URL de tu sitio web"
              value={websiteUrl}
              variant="outlined"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleCopyToClipboard}
                      edge="end"
                      sx={{
                        color: "primary.main",
                        "&:hover": { bgcolor: "rgba(255, 119, 13, 0.1)" },
                      }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "grey.50",
                },
              }}
            />
          </Box>

          {/* Botones */}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={handleOpenWebsite}
              sx={{
                borderColor: "primary.main",
                color: "primary.main",
                "&:hover": {
                  borderColor: "primary.dark",
                  bgcolor: "rgba(255, 119, 13, 0.1)",
                },
              }}
            >
              Ve a tu sitio web
            </Button>
            <Button
              variant="contained"
              onClick={handleFinishOnboarding}
              sx={{
                bgcolor: "primary.main",
                color: "white",
                fontWeight: 600,
                px: 3,
                "&:hover": {
                  bgcolor: "primary.dark",
                },
              }}
            >
              Finalizar
            </Button>
          </Box>
        </Box>
      </Modal>
    </Drawer>
  );
};

export default OnboardingSheet;
